import { addQueryParam } from "@/lib/utils";
import { useQuery, UseQueryResult } from "@tanstack/react-query";
import { AxiosResponse } from "axios";
import { useEffect, useMemo, useState } from "react";
import {
  AI_CAP_STATUS_LOCAL_STORAGE_KEY,
  apiClient,
  type ApiPaginatedResponse,
  type ApiResponse,
  isApiError,
  useInfiniteQuery,
} from "./_client";
import { type UserInvitationPaginatedResponse } from "./invitations";
import { type User } from "./user";

export interface PublicOrganization {
  name: string;
  id: string;
  organization_class: string;
  website: string;
  authorized_signatory?: User[];
}

export interface SharedOrganization extends PublicOrganization {
  contact: User[];
}

export interface TaxLiabilityType {
  year: number;
  q1: number;
  q2: number;
  q3: number;
  q4: number;
}

export interface Organization {
  name: string;
  slug: string;
  id: string;
  user_count: number;
  users: string[];
  organization_class: "buyer" | "seller";
  permissions?: string[];
  is_admin?: boolean;
  tax_liabilities?: TaxLiabilityType[];
  user_meta: {
    is_admin: boolean;
    questions_access: "none" | "read_only" | "read_write";
    feedback_access: "none" | "own" | "read" | "manage";
    projects: boolean;
    projects_all_folders: boolean;
    enable_projects_fine_grained_selection: boolean;
    portfolios: boolean;
    enable_portfolios_fine_grained_selection: boolean;
  };
  cf_fee: {
    threshold: number;
    under_threshold: number;
    above_threshold: number;
    minimum: number;
  };
  ai_ingestion_enabled: boolean;
  ai_auto_summarization: boolean;
  graph_rag_enabled: boolean;
  graph_update_mode: GraphUpdateMode;
  logo_url: string | null;
  whitelabel_enabled: boolean;
  use_logo_as_platform_brand: boolean;
  product_name: string;
}

export type GraphUpdateMode = "immediate" | "batched" | "manual";

export interface ProjectGraphSettings {
  graph_rag_enabled: boolean;
  graph_update_mode: GraphUpdateMode;
}

type OrganizationPaginatedResponse = ApiPaginatedResponse<Organization>;

type AccessLevel = "none" | "read_only" | "read_write" | "admin";

export type ProjectSelection = {
  project_id: string;
  access: AccessLevel;
};

export type PortfolioSelection = {
  portfolio_id: string;
  access: AccessLevel;
  some_projects: boolean;
  project_ids: string[];
};

type ProjectCategorySelection = {
  project_category_id: string;
  project_id: string;
  all_children: boolean;
};
type PortfolioCategorySelection = {
  portfolio_category_id: string;
  portfolio_id: string;
  all_children: boolean;
};

export interface CategoryPermissionsPayload {
  project_category_selection?: ProjectCategorySelection[];
  portfolio_category_selection?: PortfolioCategorySelection[];
}
interface CreateUserPermissions extends CategoryPermissionsPayload {
  invitation_email: string;
  is_admin: boolean;
  projects: boolean;
  projects_all_folders: boolean;
  enable_projects_fine_grained_selection: boolean;
  project_selection: ProjectSelection[];
  portfolio_selection: PortfolioSelection[];
  questions_access: AccessLevel;
}

export type OrgUserPermissions = CreateUserPermissions & { id: string };

export interface OrganizationUser {
  id: string;
  user_id: string;
  email: string;
  invitation_email: string;
  first_name: string;
  last_name: string;
  is_admin: boolean;
  permissions: OrgUserPermissions;
  portfolios: string[];
  projects: string[];
  org_name?: string;
  created_at: string;
}

interface CreateOrgUser {
  invitation_email: string;
  permissions: CreateUserPermissions;
  shared_chats?: string[];
}

type OrganizationUserPaginatedResponse = ApiPaginatedResponse<OrganizationUser>;

export const useOrganizations =
  (): UseQueryResult<OrganizationPaginatedResponse> => {
    const q = useQuery<OrganizationPaginatedResponse>({
      queryKey: ["organizations"],
      queryFn: async () => {
        const response = await apiClient.get<
          never,
          AxiosResponse<OrganizationPaginatedResponse>
        >("api/organizations/?page_size=1000");
        response.data.results.forEach((o: Organization) => {
          o.is_admin = o.user_meta.is_admin;
        });
        return response.data;
      },
    });
    return q;
  };

export const createOrganization = async ({
  name,
  organization_class,
}: {
  name: string;
  organization_class?: string;
}): Promise<ApiResponse<Organization>> => {
  const response = await apiClient.post(`api/organizations/`, {
    name,
    organization_class,
  });
  return response;
};

export const useOrganizationUsers = ({
  invitations,
  page,
}: {
  invitations?: boolean;
  page: number;
}): UseQueryResult<OrganizationUserPaginatedResponse> => {
  const url = `api/organizations/users/?invitations=${invitations ? "true" : "false"}&page=${page}`;
  const q = useQuery<OrganizationUserPaginatedResponse>({
    queryKey: ["organization-users", invitations, page],
    refetchInterval: 1000 * 60 * 5,
    refetchOnWindowFocus: true,
    queryFn: async () => {
      const response = await apiClient.get<
        never,
        AxiosResponse<OrganizationUserPaginatedResponse>
      >(url);
      return response.data;
    },
  });
  return q;
};

export const useOrganizationUsersInfinite = ({
  invitations,
  any,
}: {
  invitations?: boolean;
  any?: boolean;
}) => {
  const query = useInfiniteQuery<OrganizationUser>({
    queryKey: ["organization-users", invitations],
    queryFn: async ({ pageParam }) => {
      let url = `api/organizations/users/?invitations=${invitations ? "true" : "false"}&any=${any ? "true" : "false"}`;

      url =
        typeof pageParam === "number" ? url + `&page=${pageParam}` : pageParam;

      const response =
        await apiClient.get<ApiPaginatedResponse<OrganizationUser>>(url);
      return response.data;
    },
    initialPageParam: 1,
    getPreviousPageParam: (firstPage) => {
      return firstPage?.previous ?? undefined;
    },
    getNextPageParam: (lastPage) => {
      return lastPage?.next ?? undefined;
    },
    refetchOnWindowFocus: false,
    refetchOnMount: false,
    staleTime: Infinity,
  });
  return query;
};

export const removeUserFromOrganization = async (
  orgUserId: string,
): Promise<ApiResponse> => {
  const response = await apiClient.delete(
    `api/organizations/users/${orgUserId}/`,
  );
  return response;
};

export const setPermissions = async ({
  userId,
  permissions,
}: {
  userId: string;
  permissions: CreateUserPermissions | CategoryPermissionsPayload;
}): Promise<ApiResponse<OrganizationUser>> => {
  const response = await apiClient.patch(`api/organizations/users/${userId}/`, {
    permissions,
  });
  return response;
};

export const inviteUser = async (
  data: CreateOrgUser,
): Promise<ApiResponse<OrganizationUser>> => {
  const response = await apiClient.post<OrganizationUser>(
    `api/organizations/users/`,
    data,
  );
  return response;
};

export const useSentOrganizationInvitations = (
  page: number,
  portfolioId?: string,
  projectId?: string,
  chatId?: string,
): UseQueryResult<UserInvitationPaginatedResponse> => {
  let url = `api/invitations/user/?page=${page}`;
  if (portfolioId) {
    url += `&portfolio=${portfolioId}`;
  } else if (projectId) {
    url += `&project=${projectId}`;
  }
  if (chatId) {
    url = addQueryParam(url, "chat", chatId);
  }
  const q = useQuery<UserInvitationPaginatedResponse>({
    queryKey: [
      "organization-user-invitations",
      portfolioId,
      projectId,
      chatId,
    ],
    refetchInterval: 1000 * 60 * 5,
    refetchOnWindowFocus: true,
    queryFn: async () => {
      const response = await apiClient.get<
        never,
        AxiosResponse<UserInvitationPaginatedResponse>
      >(url);
      return response.data;
    },
  });
  return q;
};

export const removeSentOrganizationInvitation = async (
  invitationId: string,
): Promise<ApiResponse> => {
  const response = await apiClient.delete(
    `api/invitations/user/${invitationId}/`,
  );
  return response;
};

export const patchOrganization = async (
  organization: Organization,
): Promise<ApiResponse<Organization>> => {
  const response = await apiClient.patch<never, ApiResponse<Organization>>(
    `api/organizations/${organization.id}/`,
    organization,
  );
  return response;
};

export const patchProjectGraphSettings = async (
  organizationId: string,
  settings: ProjectGraphSettings,
): Promise<ApiResponse<Organization>> => {
  const response = await apiClient.patch<never, ApiResponse<Organization>>(
    `api/organizations/${organizationId}/`,
    settings,
  );
  return response;
};

export const uploadOrganizationLogo = async (
  organizationId: string,
  logo: File,
): Promise<ApiResponse<Organization>> => {
  const form = new FormData();
  form.append("logo", logo);
  const response = await apiClient.post<never, ApiResponse<Organization>>(
    `api/organizations/${organizationId}/logo/`,
    form,
    { headers: { "Content-Type": "multipart/form-data" } },
  );
  return response;
};

export const removeOrganizationLogo = async (
  organizationId: string,
): Promise<ApiResponse<Organization>> => {
  const response = await apiClient.delete<never, ApiResponse<Organization>>(
    `api/organizations/${organizationId}/logo/`,
  );
  return response;
};

export const useUserAccessList = (
  page: number,
  portfolioId?: string,
  projectId?: string,
  chatId?: string,
): UseQueryResult<
  ApiPaginatedResponse<{
    email: string;
  }>
> => {
  let url = `api/organizations/users/access-list/?page=${page}`;
  if (portfolioId) {
    url += `&portfolio=${portfolioId}`;
  } else if (projectId) {
    url += `&project=${projectId}`;
  }
  if (chatId) {
    url = addQueryParam(url, "chat", chatId);
  }
  const q = useQuery<
    ApiPaginatedResponse<{
      email: string;
    }>
  >({
    queryKey: ["user-access-list", { portfolioId, projectId, chatId }],
    refetchInterval: 1000 * 60 * 5,
    refetchOnWindowFocus: false,
    queryFn: async () => {
      const response = await apiClient.get<
        never,
        AxiosResponse<
          ApiPaginatedResponse<{
            email: string;
          }>
        >
      >(url);
      return response.data;
    },
  });
  return q;
};

export const removeIndividualAccess = async (
  userId: string,
  mode: string,
  itemId: string,
): Promise<ApiResponse> => {
  const response = await apiClient.delete(
    `api/organizations/users/${userId}/access-list/?${mode}=${itemId}`,
  );
  return response;
};

export interface CategoryAccessListItem {
  id: string;
  user_id: string;
  first_name: string;
  last_name: string;
  email: string;
  invitation_email: string;
  reason: string;
}

export const useCategoryAccessList = ({
  categoryType,
  categoryId,
  projectOrPortfolioId,
  isAdmin,
}: {
  categoryType: "project" | "portfolio";
  categoryId?: string;
  projectOrPortfolioId: string;
  isAdmin?: boolean;
}): UseQueryResult<CategoryAccessListItem[]> => {
  const q = useQuery<CategoryAccessListItem[]>({
    queryKey: ["access-list", categoryType, categoryId, projectOrPortfolioId],
    enabled: !!categoryId && !!projectOrPortfolioId && isAdmin,
    queryFn: async () => {
      const response = await apiClient.get<
        never,
        ApiResponse<CategoryAccessListItem[]>
      >(
        `api/documents/categories/${categoryType}/${categoryId}/access-list/${projectOrPortfolioId}/`,
      );
      if (isApiError(response)) throw response.error;
      return response.data;
    },
  });
  return q;
};

export const removeCategoryAccess = async ({
  categoryType,
  categoryId,
  projectOrPortfolioId,
  userId,
  accessLevel,
}: {
  categoryType: "project" | "portfolio";
  categoryId: string;
  projectOrPortfolioId: string;
  userId: string;
  accessLevel?: string;
}): Promise<ApiResponse> => {
  const response = await apiClient.delete(
    `api/documents/categories/${categoryType}/${categoryId}/access-list/${projectOrPortfolioId}/`,
    {
      data: {
        user_id: userId,
        access_level: accessLevel,
      },
    },
  );
  return response;
};

export interface GlobalSearchResultItem {
  id: string;
  title: string;
  type:
    | "Project"
    | "Portfolio"
    | "Document"
    | "Answer"
    | "Question"
    | "Category"
    | "Tag"
    | "Analysis"
    | "Report";
  description: string[];
  link: string;
}

export interface GlobalSearchResult {
  term: string;
  results: GlobalSearchResultItem[];
}

export const useGlobalSearch = ({
  term,
  resultTypes = [
    "Project",
    "Portfolio",
    "Document",
    "Answer",
    "Question",
    "Analysis",
    "Report",
  ],
}: {
  term: string;
  resultTypes?: GlobalSearchResultItem["type"][];
}): UseQueryResult<GlobalSearchResult> => {
  const q = useQuery<GlobalSearchResult>({
    queryKey: ["global-search", term],
    enabled: !!term,
    queryFn: async () => {
      const typesParam = resultTypes.length
        ? `&types=${resultTypes.join(",")}`
        : "";
      const response = await apiClient.get<
        never,
        ApiResponse<GlobalSearchResult>
      >(`api/search/?term=${term}${typesParam}`);
      if (isApiError(response)) throw response.error;
      return response.data;
    },
  });
  return q;
};

type DisabledCostCap = {
  enabled: false;
};

export type CostCapInfo =
  | DisabledCostCap
  | {
      enabled: true;
      soft_limit: number;
      hard_limit: number;
      current_spend: number;
      spend_period: "monthly" | "yearly";
      period_start: Date;
      status: {
        type: AICapStatusType;
        percentage: number;
      };
    };

export interface AIUsageResponse {
  organization_id: string;
  organization_name: string;
  date_range: {
    start_date?: string;
    end_date?: string;
  };
  total_cost: number;
  cost_cap: CostCapInfo;
}

type AICapStatusType = "normal" | "warning" | "exceeded";

interface AICapStatusData {
  type: AICapStatusType;
  timestamp: string;
}

// Status messages as constants - same whether from API or headers
const AI_CAP_STATUS_MESSAGES = {
  warning: "Approaching AI spend limit for this period.",
  exceeded:
    "AI spend limit reached or exceeded for this period. Most AI features will be disabled.",
} as const;

function getStatusMessage(type: AICapStatusType): string | undefined {
  if (type === "warning") {
    return AI_CAP_STATUS_MESSAGES.warning;
  }
  if (type === "exceeded") {
    return AI_CAP_STATUS_MESSAGES.exceeded;
  }
  return undefined;
}

export interface AIUsageData {
  organizationId: string;
  organizationName: string;
  dateRange: {
    start_date?: string;
    end_date?: string;
  };
  totalCost: number;
  costCap: {
    enabled: boolean;
    soft_limit?: number;
    hard_limit?: number;
    current_spend?: number;
    spend_period?: "monthly" | "yearly";
    status: {
      type: AICapStatusType;
      message?: string;
      percentage: number;
    };
  };
  costCapReached: boolean;
  isLoading: boolean;
  isError: boolean;
  error: unknown;
}

/**
 * Lightweight hook that only checks AI cap status from localStorage without making API calls.
 * Use this for simple status checks (banners, warnings, etc.)
 * Use useAIUsage() for detailed usage data and metrics.
 */
export const useAICapStatus = (): {
  costCapReached: boolean;
  type: AICapStatusType;
  message?: string;
} => {

  const [headerStatus, setHeaderStatus] = useState<AICapStatusData | null>(
    () => {
      const stored = localStorage.getItem(AI_CAP_STATUS_LOCAL_STORAGE_KEY);
      return stored ? JSON.parse(stored) : null;
    },
  );

  useEffect(() => {

    const handleStorageChange = (e: StorageEvent) => {
      if (e.key === AI_CAP_STATUS_LOCAL_STORAGE_KEY && e.newValue) {
        try {
          setHeaderStatus(JSON.parse(e.newValue));
        } catch {
          // Ignore parse errors
        }
      }
    };

    const handleAICapStatusChange = () => {
      const stored = localStorage.getItem(AI_CAP_STATUS_LOCAL_STORAGE_KEY);
      if (stored) {
        try {
          setHeaderStatus(JSON.parse(stored));
        } catch {
          // Ignore parse errors
        }
      }
    };

    window.addEventListener("storage", handleStorageChange);
    window.addEventListener("AICapStatus", handleAICapStatusChange);

    return () => {
      window.removeEventListener("storage", handleStorageChange);
      window.removeEventListener("AICapStatus", handleAICapStatusChange);
    };
  }, []);

  const type: AICapStatusType = headerStatus?.type || "normal";
  const costCapReached = type === "exceeded";


  // useMemo to prevent creating new object on every render
  return useMemo(
    () => ({
      costCapReached,
      type,
      message: getStatusMessage(type),
    }),
    [costCapReached, type],
  );
};

/**
 * Hook for fetching detailed AI usage data and metrics.
 * Only use this in views that display usage analytics (e.g., AI Usage tab).
 * For simple status checks, use useAICapStatus() instead.
 */
export const useAIUsage = ({
  organizationId,
  startDate,
  endDate,
}: {
  organizationId: string;
  startDate?: string;
  endDate?: string;
}): AIUsageData => {
  // Get header status type to trigger refetches when it changes
  const { type: headerStatusType } = useAICapStatus();

  // Track previous headerType to detect changes
  const [prevHeaderType, setPrevHeaderType] = useState<AICapStatusType | null>(null);

  const q = useQuery<AIUsageResponse>({
    queryKey: ["ai-usage", organizationId, startDate, endDate],
    enabled: !!organizationId,
    refetchOnWindowFocus: true, // Refetch when user returns to the tab
    refetchOnMount: true, // Refetch when navigating to the AI Usage tab
    staleTime: 1000 * 30, // Consider data fresh for 30 seconds (prevents excessive refetches)
    queryFn: async () => {
      let url = `api/organizations/${organizationId}/ai-usage/`;
      const params = new URLSearchParams();
      if (startDate) params.append("start_date", startDate);
      if (endDate) params.append("end_date", endDate);
      if (params.toString()) url += `?${params.toString()}`;

      const response = await apiClient.get<never, ApiResponse<AIUsageResponse>>(
        url,
      );
      if (isApiError(response)) throw response.error;
      return response.data;
    },
  });

  // Refetch when header status type changes (e.g., normal -> warning -> exceeded)
  useEffect(() => {
    // If this is the first time we're seeing a headerStatusType, just store it
    if (prevHeaderType === null) {
      setPrevHeaderType(headerStatusType);
      return;
    }

    // If the headerStatusType has changed, refetch the data
    if (prevHeaderType !== headerStatusType && !q.isFetching) {
      setPrevHeaderType(headerStatusType);
      q.refetch();
    }
  }, [headerStatusType, prevHeaderType, q]);

  const statusType: AICapStatusType = q.data?.cost_cap?.enabled
    ? q.data.cost_cap.status.type
    : "normal";
  const costCapReached = statusType === "exceeded";

  // Build consolidated cost cap with full details for UI rendering
  // useMemo prevents creating new object references on every render
  const costCap = useMemo(() => {
    return q.data?.cost_cap?.enabled
      ? {
          enabled: true as const,
          soft_limit: q.data.cost_cap.soft_limit,
          hard_limit: q.data.cost_cap.hard_limit,
          current_spend: q.data.cost_cap.current_spend,
          spend_period: q.data.cost_cap.spend_period,
          status: {
            type: statusType,
            message: getStatusMessage(statusType),
            percentage: q.data.cost_cap.status.percentage,
          },
        }
      : {
          enabled: false as const,
          status: {
            type: statusType,
            message: getStatusMessage(statusType),
            percentage: 0,
          },
        };
  }, [q.data, statusType]);

  // Return clean, purpose-built data structure
  // useMemo prevents creating new object references on every render
  return useMemo(
    () => ({
      organizationId: q.data?.organization_id || "",
      organizationName: q.data?.organization_name || "",
      dateRange: q.data?.date_range || {},
      totalCost: q.data?.total_cost || 0,
      costCap,
      costCapReached,
      isLoading: q.isLoading,
      isError: q.isError,
      error: q.error,
    }),
    [q.data, costCap, costCapReached, q.isLoading, q.isError, q.error],
  );
};

// --- Global Dashboard (cross-org) types & hooks ---

export interface GlobalDashboardProject {
  id: string;
  name: string;
  document_count: number;
  last_modified: string;
}

export interface GlobalDashboardDocument {
  id: string;
  filename: string;
  created_at: string;
}

export interface GlobalDashboardReport {
  id: string;
  title: string;
  status: string;
  created_at: string;
}

export interface GlobalDashboardOrg {
  id: string;
  name: string;
  slug: string;
  projects: GlobalDashboardProject[];
  recent_documents: GlobalDashboardDocument[];
  recent_document_count: number;
  recent_reports: GlobalDashboardReport[];
}

interface GlobalDashboardData {
  metrics: {
    total_orgs: number;
    pending_reports: number;
    global_file_count: number;
  };
  organizations: GlobalDashboardOrg[];
}

export const useGlobalDashboard = () =>
  useQuery<GlobalDashboardData>({
    queryKey: ["global-dashboard"],
    queryFn: async () => {
      const response = await apiClient.get<
        never,
        AxiosResponse<GlobalDashboardData>
      >("api/organizations/global-dashboard/");
      return response.data;
    },
    staleTime: 60_000,
  });

interface GlobalProjectOrg {
  id: string;
  name: string;
  slug: string;
}

interface GlobalProjectPortfolio {
  id: string;
  title: string | null;
}

export interface GlobalProjectItem {
  id: string;
  name: string;
  is_archived: boolean;
  created_at: string;
  last_modified: string | null;
  document_count: number;
  integration_count: number;
  organization: GlobalProjectOrg;
  portfolios: GlobalProjectPortfolio[];
}

interface GlobalProjectsPage {
  count: number;
  page_size: number;
  next: string | null;
  previous: string | null;
  results: GlobalProjectItem[];
}

interface UseGlobalProjectsParams {
  organizationId?: string;
  search?: string;
  ordering?: string;
  page?: number;
  pageSize?: number;
  showArchived?: boolean;
}

export const useGlobalProjects = (params: UseGlobalProjectsParams) =>
  useQuery<GlobalProjectsPage>({
    queryKey: ["global-projects", params],
    queryFn: async () => {
      const qs = new URLSearchParams();
      if (params.organizationId) qs.set("organization_id", params.organizationId);
      if (params.search) qs.set("search", params.search);
      if (params.ordering) qs.set("ordering", params.ordering);
      if (params.page) qs.set("page", String(params.page));
      if (params.pageSize) qs.set("page_size", String(params.pageSize));
      if (params.showArchived) qs.set("show_archived", "true");
      const response = await apiClient.get<
        never,
        AxiosResponse<GlobalProjectsPage>
      >(`api/organizations/global-projects/?${qs.toString()}`);
      return response.data;
    },
    staleTime: 30_000,
  });

export type GlobalReportStatus = "running" | "completed" | "failed";

export interface GlobalReportItem {
  id: string;
  title: string;
  status: GlobalReportStatus;
  created_at: string;
  analysis: { id: string; title: string };
  organization: GlobalProjectOrg;
}

interface GlobalReportsPage {
  count: number;
  page_size: number;
  next: string | null;
  previous: string | null;
  results: GlobalReportItem[];
}

export interface UseGlobalReportsParams {
  organizationId?: string;
  search?: string;
  status?: GlobalReportStatus;
  ordering?: string;
  page?: number;
  pageSize?: number;
}

export const useGlobalReports = (params: UseGlobalReportsParams) =>
  useQuery<GlobalReportsPage>({
    queryKey: ["global-reports", params],
    queryFn: async () => {
      const qs = new URLSearchParams();
      if (params.organizationId) qs.set("organization_id", params.organizationId);
      if (params.search) qs.set("search", params.search);
      if (params.status) qs.set("status", params.status);
      if (params.ordering) qs.set("ordering", params.ordering);
      if (params.page) qs.set("page", String(params.page));
      if (params.pageSize) qs.set("page_size", String(params.pageSize));
      const response = await apiClient.get<
        never,
        AxiosResponse<GlobalReportsPage>
      >(`api/organizations/global-reports/?${qs.toString()}`);
      return response.data;
    },
    staleTime: 30_000,
  });

export interface GlobalDashboardListItem {
  id: string;
  name: string;
  description: string;
  is_template: boolean;
  created_at: string;
  updated_at: string;
  organization: GlobalProjectOrg;
}

interface GlobalDashboardsListPage {
  count: number;
  page_size: number;
  next: string | null;
  previous: string | null;
  results: GlobalDashboardListItem[];
}

export interface UseGlobalDashboardsParams {
  organizationId?: string;
  search?: string;
  includeTemplates?: boolean;
  ordering?: string;
  page?: number;
  pageSize?: number;
}

export const useGlobalDashboardsList = (params: UseGlobalDashboardsParams) =>
  useQuery<GlobalDashboardsListPage>({
    queryKey: ["global-dashboards", params],
    queryFn: async () => {
      const qs = new URLSearchParams();
      if (params.organizationId) qs.set("organization_id", params.organizationId);
      if (params.search) qs.set("search", params.search);
      if (params.includeTemplates === false) qs.set("include_templates", "false");
      if (params.ordering) qs.set("ordering", params.ordering);
      if (params.page) qs.set("page", String(params.page));
      if (params.pageSize) qs.set("page_size", String(params.pageSize));
      const response = await apiClient.get<
        never,
        AxiosResponse<GlobalDashboardsListPage>
      >(`api/organizations/global-dashboards/?${qs.toString()}`);
      return response.data;
    },
    staleTime: 30_000,
  });
